/**
 * Unauthorized Access Page - WMS DAOUXUN
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Avatar,
  Stack
} from '@mui/material';
import {
  Block as BlockIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const UnauthorizedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { from, requiredRoles, requiredPermissions, userRole } = location.state || {};

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/client/dashboard');
  };

  const handleContactAdmin = () => {
    if (typeof window !== 'undefined' && window?.enqueueSnackbar) {
      window.enqueueSnackbar(t('unauthorized.contactAdminAction', 'Request sent to administrator'), { variant: 'info' });
      return;
    }
    try {
      const { enqueueSnackbar } = require('notistack');
      enqueueSnackbar(t('unauthorized.contactAdminAction', 'Request sent to administrator'), { variant: 'info' });
    } catch (error) { console.error(error); }
  };

  const getRoleLabel = (role) => {
    return t(`users.permissions.${role}`, role);
  };

  return (
    <Box
      data-testid="access-denied"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          borderRadius: 3
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header with icon */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'error.main',
                mx: 'auto',
                mb: 2
              }}
            >
              <BlockIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" component="h1" gutterBottom color="error">
              {t('unauthorized.title', 'Access Denied')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('unauthorized.subtitle', "You don't have the required permissions to access this page")}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Detailed information */}
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Label:</strong> {t('unauthorized.accessDeniedMessage', "Your current permissions do not allow access to this resource.")}
              </Typography>
            </Alert>

            {from && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Label:</strong> {from}
                </Typography>
              </Alert>
            )}

            {userRole && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('unauthorized.currentRole', 'Your current role:')}
                </Typography>
                <Chip
                  label={getRoleLabel(userRole)}
                  color="primary"
                  variant="outlined"
                  icon={<SecurityIcon />}
                />
              </Box>
            )}

            {requiredRoles && requiredRoles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('unauthorized.requiredRoles', 'Roles required for this page:')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {requiredRoles.map((role, index) => (
                    <Chip
                      key={index}
                      label={getRoleLabel(role)}
                      color="error"
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {requiredPermissions && requiredPermissions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('unauthorized.requiredPermissions', 'Required permissions:')}
                </Typography>
                <List dense>
                  {requiredPermissions.map((permission, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={permission}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Suggested solutions */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('unauthorized.suggestionsTitle', 'Suggested solutions:')}
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('unauthorized.suggestions.contactAdmin', 'Contact your system administrator')}
                  secondary={t('unauthorized.suggestions.contactAdminSub', 'Request assignment of the required permissions')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('unauthorized.suggestions.checkRole', 'Verify your user role')}
                  secondary={t('unauthorized.suggestions.checkRoleSub', 'Make sure you are logged in with the correct account')}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={t('unauthorized.suggestions.goDashboard', 'Return to your dashboard')}
                  secondary={t('unauthorized.suggestions.goDashboardSub', 'Access features allowed for your role')}
                />
              </ListItem>
            </List>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleGoBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
            >
              Dashboard
          </Button>
            <Button
              variant="outlined"
              color="info"
              startIcon={<InfoIcon />}
              onClick={handleContactAdmin}
            >
              {t('unauthorized.contactAdmin', 'Contact Admin')}
          </Button>
          </Stack>

          {/* System information */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('unauthorized.systemName', 'WMS DAOUXUN - Warehouse Management System')}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              {t('unauthorized.errorCode403', 'Error code: 403 - Access forbidden')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default React.memo(UnauthorizedPage);

